import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Paper, Typography, Box, Grid, Divider, List, ListItem, ListItemIcon, ListItemText, ListItemButton, Snackbar, Alert, Tabs, Tab, Switch, FormControlLabel
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PolicyIcon from '@mui/icons-material/Policy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { initNotifications, revokeNotifications } from '../../../../services/notificationService';
import { Link } from 'react-router-dom';
import { addBankDetails, getBankDetails } from "../../../../api/services/userServices";
import { getSubscriptionStatus } from '../../../../api/services/pgTenantService';
import { useUserContext } from '../../../protectedRoute/UserContext';
import { Zap, Crown, Users, ArrowUpRight, Loader2 } from 'lucide-react';

interface BankDetails {
    masked_account_number: string;
    ifsc_code: string;
}

const PLAN_META: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ElementType }> = {
    free:    { label: 'Free',    color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: Users  },
    basic:   { label: 'Basic',   color: '#276859', bg: '#f0f7f4', border: '#276859', icon: Zap    },
    premium: { label: 'Premium', color: '#b45309', bg: '#fffbeb', border: '#FBBD23', icon: Crown  },
    active:  { label: 'Premium', color: '#b45309', bg: '#fffbeb', border: '#FBBD23', icon: Crown  },
    expired: { label: 'Expired', color: '#9ca3af', bg: '#f3f4f6', border: '#d1d5db', icon: Users  },
};

interface SettingsProps {
    onGoToSubscription?: () => void;
}

const Settings: React.FC<SettingsProps> = ({ onGoToSubscription }) => {
    const { state } = useUserContext();
    const { hostID, organization_id } = state;
    const [open, setOpen] = useState(false);
    const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    const [newBankAccountNumber, setNewBankAccountNumber] = useState('');
    const [newIfscCode, setNewIfscCode] = useState('');
    const [newAdditionalFields, setNewAdditionalFields] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const [subStatus, setSubStatus] = useState<any>(null);
    const [subLoading, setSubLoading] = useState(false);

    const [pushEnabled, setPushEnabled] = useState<boolean>(
        localStorage.getItem('setting_push_notifications') !== 'false'
    );

    const handlePushToggle = async (enabled: boolean) => {
        setPushEnabled(enabled);
        localStorage.setItem('setting_push_notifications', String(enabled));
        if (enabled) {
            await initNotifications();
        } else {
            await revokeNotifications();
        }
    };

    useEffect(() => {
        if (organization_id) {
            getBankDetails(organization_id).then((data) => {
                if (data) setBankDetails(data);
            });
        }
    }, [organization_id]);

    const handleClickOpen = () => {
        setNewBankAccountNumber('');
        setNewIfscCode(bankDetails?.ifsc_code || '');
        setNewAdditionalFields('');
        setOpen(true);
    };

    const handleClose = () => setOpen(false);
    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarOpen(false);
    };

    

    const handleSubmit = async () => {
        if (!hostID) {
            setSnackbarMessage("Host ID is missing. Please log in again.");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        const accountNumber = (newBankAccountNumber || '').trim().replace(/\s+/g, '');
        const ifscCode = (newIfscCode || '').trim().toUpperCase();

        if (!/^\d{9,18}$/.test(accountNumber)) {
            setSnackbarMessage("Enter a valid bank account number (9 to 18 digits, numbers only).");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
            setSnackbarMessage("Enter a valid IFSC code (e.g. HDFC0001234).");
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        const updatedDetails = {
            user: hostID,
            bank_account_number: accountNumber,
            ifsc_code: ifscCode,
            additional_fields: newAdditionalFields
        };

        try {
            const response = await addBankDetails(updatedDetails);
            setOpen(false);
            if(response?.id){
                setSnackbarMessage("Bank details updated successfully!");
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                if (organization_id) {
                    getBankDetails(organization_id).then((data) => {
                        if (data) setBankDetails(data);
                    });
                }
            } else {
                setSnackbarMessage("Failed to update bank details.");
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            setSnackbarMessage("Failed to update bank details.");
            setSnackbarSeverity('error');
            setSnackbarOpen(true)
        }
    };

    useEffect(() => {
        if (activeTab === 1) {
            setSubLoading(true);
            getSubscriptionStatus()
                .then(setSubStatus)
                .catch(() => setSubStatus(null))
                .finally(() => setSubLoading(false));
        }
    }, [activeTab]);

    return (
        <Box className="bg-gray-50 p-4">
            <Paper elevation={3} className="p-8 rounded-lg">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)} aria-label="settings tabs">
                            <Tab label="Account" />
                            <Tab label="Subscription" />
                            <Tab label="Policies" />
                        </Tabs>
                        <Divider sx={{ mb: 3 }} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        {activeTab === 0 && ( // Account Tab
                            <Box>
                                <Typography variant="h6" gutterBottom>Account Information</Typography>
                                <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5, color: '#276859' }}>
                                        Linked Bank Account
                                    </Typography>
                                    {bankDetails ? (
                                        <>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                <strong>Account Number:</strong> {bankDetails.masked_account_number || 'Not provided'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>IFSC Code:</strong> {bankDetails.ifsc_code || 'Not provided'}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No bank account linked yet.</Typography>
                                    )}
                                </Box>
                                <List>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={handleClickOpen}>
                                            <ListItemIcon><CreditCardIcon /></ListItemIcon>
                                            <ListItemText primary="Update Bank Details" secondary="Update your bank account information" />
                                        </ListItemButton>
                                    </ListItem>
                                </List>

                                {/* Notification Settings */}
                                <Box sx={{ mt: 3, p: 2.5, borderRadius: 2, border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#276859' }}>
                                        Notifications
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <NotificationsIcon sx={{ color: '#276859', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>Push Notifications</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Get notified when a tenant raises a ticket or submits a rating
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Switch
                                            checked={pushEnabled}
                                            onChange={(e) => handlePushToggle(e.target.checked)}
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#276859' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#276859' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {activeTab === 1 && (
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ color: '#276859', fontWeight: 'bold' }}>
                                    Subscription Plan
                                </Typography>

                                {subLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#276859' }} />
                                    </Box>
                                ) : subStatus ? (() => {
                                    const plan = subStatus.plan ?? 'free';
                                    const meta = PLAN_META[plan] ?? PLAN_META.free;
                                    const PlanIcon = meta.icon;
                                    const tenantPct = subStatus.max_tenants
                                        ? Math.min((subStatus.tenant_count / subStatus.max_tenants) * 100, 100)
                                        : null;

                                    return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Plan badge card */}
                                            <Box
                                                sx={{
                                                    p: 3, borderRadius: 3, border: `2px solid ${meta.border}`,
                                                    background: meta.bg, display: 'flex', alignItems: 'center', gap: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        p: 1.5, borderRadius: 2,
                                                        backgroundColor: `${meta.color}18`,
                                                        display: 'flex', alignItems: 'center',
                                                    }}
                                                >
                                                    <PlanIcon size={22} color={meta.color} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6" sx={{ color: meta.color, fontWeight: 'bold', lineHeight: 1 }}>
                                                        {meta.label} Plan
                                                    </Typography>
                                                    {subStatus.renewal_date && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            Renews on {new Date(subStatus.renewal_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {plan !== 'free' && (
                                                    <Box
                                                        sx={{
                                                            px: 1.5, py: 0.4, borderRadius: 10,
                                                            backgroundColor: '#FBBD23', color: '#276859',
                                                            fontSize: 11, fontWeight: 'bold',
                                                        }}
                                                    >
                                                        ACTIVE
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Usage stats */}
                                            <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb', background: '#fff', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {/* Tenant usage */}
                                                <Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>Long-term Tenants</Typography>
                                                        <Typography variant="body2" sx={{ color: '#276859', fontWeight: 'bold' }}>
                                                            {subStatus.tenant_count} / {subStatus.max_tenants === null ? 'Unlimited' : subStatus.max_tenants}
                                                        </Typography>
                                                    </Box>
                                                    {tenantPct !== null && (
                                                        <Box sx={{ height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', overflow: 'hidden' }}>
                                                            <Box sx={{
                                                                height: '100%', borderRadius: 3,
                                                                width: `${tenantPct}%`,
                                                                backgroundColor: tenantPct >= 90 ? '#ef4444' : tenantPct >= 70 ? '#FBBD23' : '#276859',
                                                                transition: 'width 0.4s',
                                                            }} />
                                                        </Box>
                                                    )}
                                                </Box>

                                                {/* Marketplace contacts */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>Marketplace Contacts Visible</Typography>
                                                    <Typography variant="body2" sx={{ color: '#276859', fontWeight: 'bold' }}>
                                                        {subStatus.max_marketplace_contacts === null
                                                            ? 'Unlimited'
                                                            : subStatus.max_marketplace_contacts === 0
                                                                ? 'Not available'
                                                                : `Up to ${subStatus.max_marketplace_contacts}`}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Upgrade CTA */}
                                            {plan === 'free' && (
                                                <Box sx={{ p: 2.5, borderRadius: 3, background: 'linear-gradient(135deg, #276859, #2A5248)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>Upgrade to grow faster</Typography>
                                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                                            Basic from ₹199/mo · Premium from ₹399/mo
                                                        </Typography>
                                                    </Box>
                                                    <button
                                                        onClick={() => onGoToSubscription?.()}
                                                        style={{
                                                            backgroundColor: '#FBBD23', color: '#276859',
                                                            border: 'none', borderRadius: 8, padding: '8px 16px',
                                                            fontWeight: 'bold', fontSize: 13, cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Upgrade <ArrowUpRight size={14} />
                                                    </button>
                                                </Box>
                                            )}
                                            {(plan === 'basic') && (
                                                <button
                                                    onClick={() => onGoToSubscription?.()}
                                                    style={{
                                                        background: 'none', border: '1.5px solid #276859',
                                                        borderRadius: 8, padding: '8px 16px', color: '#276859',
                                                        fontWeight: 'bold', fontSize: 13, cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', gap: 4,
                                                    }}
                                                >
                                                    Upgrade to Premium <ArrowUpRight size={14} />
                                                </button>
                                            )}
                                        </Box>
                                    );
                                })() : (
                                    <Typography variant="body2" color="text.secondary">
                                        Failed to load subscription details.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {activeTab === 2 && ( // Policies Tab
                            <Box>
                                <Typography variant="h6" gutterBottom>Policies</Typography>
                                <List>
                                    <ListItem disablePadding component={Link} to="/host/policies">
                                        <ListItemButton>
                                            <ListItemIcon><PolicyIcon /></ListItemIcon>
                                            <ListItemText primary="View Policies" secondary="View our terms and conditions" />
                                        </ListItemButton>
                                    </ListItem>
                                </List>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                    <DialogTitle>Update Bank Details</DialogTitle>
                    <DialogContent className="p-4">
                        <TextField
                            autoFocus margin="dense" id="bankAccountNumber" label="Bank Account Number" type="text" fullWidth
                            value={newBankAccountNumber} onChange={(e) => setNewBankAccountNumber(e.target.value)} variant="outlined" className="mb-4"
                        />
                        <TextField
                            margin="dense" id="ifscCode" label="IFSC Code" type="text" fullWidth
                            value={newIfscCode} onChange={(e) => setNewIfscCode(e.target.value.toUpperCase())} variant="outlined"
                        />
                    </DialogContent>
                    <DialogActions className="p-4">
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleSubmit}>Submit</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Settings;